/**
 * Acceso al contenido del profesor.
 *
 * Va **directo a PostgREST**, sin pasar por el servidor de juego: ninguna de estas tablas es de
 * partida, así que el principio VI no lo prohíbe, y las políticas de la base de datos ya
 * garantizan que nadie ve el material de otro.
 *
 * El control optimista: cada actualización exige la versión que se leyó. Si no encuentra la fila,
 * alguien la cambió mientras tanto. **PostgREST devuelve en ese caso cero filas, no un error**, y
 * por eso hay que mirarlo explícitamente.
 */

import type { Role } from '@clica/shared';
import { supabase } from '../auth/supabase.js';

/**
 * Acota en un solo sitio la conversión de tipos.
 *
 * Sin tipos de base de datos generados, `supabase-js` devuelve `any` en cada consulta. Dejar que
 * ese `any` se extienda por el resto del archivo convertiría cada `fila.title` en una lectura sin
 * comprobar; aquí se acota, se documenta y se acabó. Es el mismo patrón que usa `apps/game`.
 */
function rows<T>(data: unknown): readonly T[] {
  return (data ?? []) as readonly T[];
}

function row<T>(data: unknown): T {
  return data as T;
}

function maybeRow<T>(data: unknown): T | null {
  return (data ?? null) as T | null;
}

export interface Subject {
  readonly id: string;
  readonly owner_id: string;
  readonly name: string;
  readonly color: string;
  readonly archived_at: string | null;
  readonly version: number;
}

export interface Quiz {
  readonly id: string;
  readonly subject_id: string;
  readonly owner_id: string;
  readonly title: string;
  readonly description: string | null;
  readonly default_settings: Record<string, unknown>;
  readonly forked_from: string | null;
  readonly version: number;
  readonly updated_at: string;
}

export interface Question {
  readonly id: string;
  readonly quiz_id: string;
  readonly position: number;
  readonly type: string;
  readonly prompt_md: string | null;
  readonly hint_md: string | null;
  readonly time_limit_s: number | null;
  readonly hint_after_s: number | null;
  readonly shuffle_options: boolean | null;
  readonly points_multiplier: number | null;
  /** La ruta en el bucket, no una URL: las firmadas caducan a las dos horas. */
  readonly image_path: string | null;
  readonly image_alt: string | null;
  readonly image_width: number | null;
  readonly image_height: number | null;
  readonly version: number;
}

export interface Option {
  readonly id: string;
  readonly question_id: string;
  readonly position: number;
  readonly text_md: string | null;
  readonly is_correct: boolean;
  readonly version: number;
}

/** Lo que devuelve un guardado: o la fila nueva, o que alguien se te adelantó. */
export type SaveOutcome<T> =
  | { readonly ok: true; readonly row: T }
  | { readonly ok: false; readonly reason: 'conflict' | 'failed' };

// ---------------------------------------------------------------------------
// Asignaturas
// ---------------------------------------------------------------------------

export async function listSubjects(includeArchived = false): Promise<readonly Subject[]> {
  let query = supabase.from('subjects').select('*').order('name');
  if (!includeArchived) query = query.is('archived_at', null);

  const { data, error } = await query;
  if (error !== null) throw error;
  return rows<Subject>(data);
}

/** Una asignatura por su identificador, para poder titular la pantalla con su nombre. */
export async function getSubject(id: string): Promise<Subject | null> {
  const result = await supabase.from('subjects').select('*').eq('id', id).maybeSingle();
  if (result.error !== null) throw result.error;
  return result.data === null ? null : row<Subject>(result.data);
}

export async function createSubject(
  ownerId: string,
  name: string,
  color: string,
): Promise<Subject> {
  const result = await supabase
    .from('subjects')
    .insert({ owner_id: ownerId, name, color })
    .select()
    .single();

  if (result.error !== null) throw result.error;
  return row<Subject>(result.data);
}

export async function archiveSubject(id: string, archived: boolean): Promise<void> {
  const { error } = await supabase
    .from('subjects')
    .update({ archived_at: archived ? new Date().toISOString() : null })
    .eq('id', id);

  if (error !== null) throw error;
}

/** Cuántos cuestionarios se llevaría por delante borrar esta asignatura (RF-024). */
export async function countQuizzesIn(subjectId: string): Promise<number> {
  const { count, error } = await supabase
    .from('quizzes')
    .select('id', { count: 'exact', head: true })
    .eq('subject_id', subjectId);

  if (error !== null) throw error;
  return count ?? 0;
}

export async function deleteSubject(id: string): Promise<void> {
  const { error } = await supabase.from('subjects').delete().eq('id', id);
  if (error !== null) throw error;
}

// ---------------------------------------------------------------------------
// Cuestionarios
// ---------------------------------------------------------------------------

export async function listQuizzes(subjectId: string): Promise<readonly Quiz[]> {
  const { data, error } = await supabase
    .from('quizzes')
    .select('*')
    .eq('subject_id', subjectId)
    .order('updated_at', { ascending: false });

  if (error !== null) throw error;
  return rows<Quiz>(data);
}

export async function getQuiz(id: string): Promise<Quiz | null> {
  const result = await supabase.from('quizzes').select('*').eq('id', id).maybeSingle();
  if (result.error !== null) throw result.error;
  return maybeRow<Quiz>(result.data);
}

export async function createQuiz(ownerId: string, subjectId: string, title: string): Promise<Quiz> {
  const result = await supabase
    .from('quizzes')
    .insert({ owner_id: ownerId, subject_id: subjectId, title })
    .select()
    .single();

  if (result.error !== null) throw result.error;
  return row<Quiz>(result.data);
}

export async function deleteQuiz(id: string): Promise<void> {
  const { error } = await supabase.from('quizzes').delete().eq('id', id);
  if (error !== null) throw error;
}

// ---------------------------------------------------------------------------
// Preguntas y opciones
// ---------------------------------------------------------------------------

export async function listQuestions(quizId: string): Promise<readonly Question[]> {
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .eq('quiz_id', quizId)
    .order('position');

  if (error !== null) throw error;
  return rows<Question>(data);
}

/**
 * Las preguntas de VARIOS cuestionarios de una vez.
 *
 * Existe para no repetir una consulta por cuestionario. La lista de una asignatura necesita saber
 * si cada uno está listo para jugarse, y hacerlo de uno en uno costaba 1+2N peticiones: con veinte
 * cuestionarios, cuarenta y una, en serie, y ninguna fila aparecía hasta que volvía la última.
 */
export async function listQuestionsOf(quizIds: readonly string[]): Promise<readonly Question[]> {
  if (quizIds.length === 0) return [];

  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .in('quiz_id', [...quizIds])
    .order('position');

  if (error !== null) throw error;
  return rows<Question>(data);
}

export async function listOptions(questionIds: readonly string[]): Promise<readonly Option[]> {
  if (questionIds.length === 0) return [];

  const { data, error } = await supabase
    .from('options')
    .select('*')
    .in('question_id', [...questionIds])
    .order('position');

  if (error !== null) throw error;
  return rows<Option>(data);
}

/**
 * Crea una pregunta con sus dos opciones iniciales.
 *
 * El tipo se escribe explícitamente porque la columna no tiene valor por defecto: el
 * conocimiento de qué tipos existen vive en `packages/shared`, no en el esquema (principio VII).
 */
/**
 * La siguiente posición libre de un cuestionario.
 *
 * No vale con contar cuántas preguntas hay. `position` tiene una restricción única por
 * cuestionario, y borrar la pregunta del medio deja un hueco: con 0, 1 y 2, al borrar la 1 quedan
 * 0 y 2, y la siguiente pregunta nacería en la 2, que ya está ocupada. La restricción es
 * `deferrable initially deferred`, así que el choque no salta en el `insert` sino al confirmar la
 * transacción, y el mensaje llega cuando ya nadie sabe qué lo provocó.
 */
async function nextQuestionPosition(quizId: string): Promise<number> {
  const { data, error } = await supabase
    .from('questions')
    .select('position')
    .eq('quiz_id', quizId)
    .order('position', { ascending: false })
    .limit(1);

  if (error !== null) throw error;
  const filas = rows<{ position: number }>(data);
  return filas.length === 0 ? 0 : filas[0]!.position + 1;
}

export async function createQuestion(quizId: string, type: string): Promise<Question> {
  const position = await nextQuestionPosition(quizId);

  const result = await supabase
    .from('questions')
    .insert({ quiz_id: quizId, position, type })
    .select()
    .single();

  if (result.error !== null) throw result.error;
  const question = row<Question>(result.data);

  // Dos opciones para empezar, que es el mínimo del dominio. De ahí en adelante se añaden y se
  // quitan desde el editor hasta cuatro.
  const { error: optionsError } = await supabase.from('options').insert([
    { question_id: question.id, position: 0, is_correct: true },
    { question_id: question.id, position: 1, is_correct: false },
  ]);
  if (optionsError !== null) throw optionsError;

  return question;
}

/** Añade una opción al final de una pregunta. */
export async function createOption(questionId: string, position: number): Promise<Option> {
  const result = await supabase
    .from('options')
    .insert({ question_id: questionId, position, is_correct: false })
    .select()
    .single();

  if (result.error !== null) throw result.error;
  return row<Option>(result.data);
}

/**
 * Quita una opción y renumera las que quedan.
 *
 * Renumerar no es opcional: sin ello, quitar la B de cuatro deja las posiciones 0, 2 y 3, y la
 * siguiente opción que se añada choca con la restricción única. Se usa la función que ya existía
 * en la base de datos desde la primera migración, con su permiso concedido, y a la que no
 * llamaba nadie.
 */
export async function deleteOption(id: string, restantes: readonly string[]): Promise<void> {
  const { error } = await supabase.from('options').delete().eq('id', id);
  if (error !== null) throw error;
  await reorderOptions(restantes);
}

export async function reorderOptions(orderedIds: readonly string[]): Promise<void> {
  if (orderedIds.length === 0) return;
  const { error } = await supabase.rpc('reorder_options', { ordered_ids: [...orderedIds] });
  if (error !== null) throw error;
}

/**
 * Borra una pregunta y renumera las que quedan.
 *
 * Igual que con las opciones: sin renumerar quedan huecos, y el hueco convierte el botón de
 * añadir en un botón que no hace nada.
 */
export async function deleteQuestion(id: string, restantes: readonly string[] = []): Promise<void> {
  const { error } = await supabase.from('questions').delete().eq('id', id);
  if (error !== null) throw error;
  if (restantes.length > 0) await reorderQuestions(restantes);
}

/**
 * Guarda un cambio exigiendo la versión que se leyó.
 *
 * Si nadie tocó la fila, se actualiza y devuelve la nueva. Si alguien lo hizo, la condición de
 * versión no encuentra nada y PostgREST devuelve **cero filas sin error**: eso es el conflicto.
 */
export async function saveWithVersion<T>(
  table: 'subjects' | 'quizzes' | 'questions' | 'options',
  id: string,
  version: number,
  patch: Record<string, unknown>,
): Promise<SaveOutcome<T>> {
  const { data, error } = await supabase
    .from(table)
    .update(patch)
    .eq('id', id)
    .eq('version', version)
    .select();

  if (error !== null) return { ok: false, reason: 'failed' };

  const updated = rows<T>(data);
  const first = updated[0];
  if (first === undefined) return { ok: false, reason: 'conflict' };

  return { ok: true, row: first };
}

/**
 * Reordena las preguntas de un cuestionario.
 *
 * Se manda la lista entera en el orden nuevo. La unicidad de posición es **diferible**, así que
 * renumerar dentro de una transacción no revienta a mitad; sin eso, mover una pregunta de la
 * quinta posición a la segunda fallaría con clave duplicada aunque el estado final fuera válido.
 */
export async function reorderQuestions(orderedIds: readonly string[]): Promise<void> {
  const { error } = await supabase.rpc('reorder_questions', {
    ordered_ids: [...orderedIds],
  });
  if (error !== null) throw error;
}

/** El rol se lee del perfil, nunca del token (principio I). */
export type { Role };
